import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, Languages } from "lucide-react";
import { logError } from "../lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pickTranslation = (blog) => {
  const t = blog?.translations || {};
  return t.en || t.bn || Object.values(t)[0] || { title: "", synopsis: "" };
};

export default function LatestBlogCard() {
  const [blog, setBlog] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/blogs`);
        if (Array.isArray(data) && data.length > 0) {
          // /api/blogs already returns published-only sorted by publishedAt desc
          setBlog(data[0]);
        }
      } catch (e) {
        logError("Failed to load latest blog:", e);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  if (!loaded || !blog) return null;

  const tr = pickTranslation(blog);
  const cover = blog.coverImage || "";
  const date = blog.publishedAt
    ? new Date(blog.publishedAt).toLocaleDateString(undefined, {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";
  const langCount = Object.keys(blog.translations || {}).length;

  return (
    <section className="pb-20 sm:pb-24" data-testid="latest-blog-section">
      <div className="container-narrow">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="eyebrow">From the journal</p>
            <h2 className="font-serif text-3xl sm:text-4xl mt-3 text-[#2C2A28]">
              The latest entry.
            </h2>
          </div>
          <Link
            to="/blogs"
            className="text-sm tracking-wide text-[#4A6B53] hover:text-[#3A5441] inline-flex items-center gap-1.5 group"
            data-testid="latest-blog-see-all"
          >
            See all entries
            <ArrowUpRight
              size={14}
              className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-300"
            />
          </Link>
        </div>

        <motion.article
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          data-testid="latest-blog-card"
        >
          <Link
            to={`/blogs/${blog.slug}`}
            className="card-lift group block rounded-sm bg-[#F2EFEB] border border-[#D8D3CC] overflow-hidden grid md:grid-cols-2"
            data-testid="latest-blog-link"
          >
            {cover ? (
              <div className="relative overflow-hidden bg-[#EAE5DC] h-64 md:h-auto md:min-h-[20rem]">
                <img
                  src={cover}
                  alt={tr.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
            ) : (
              <div className="bg-[#EAE5DC] h-64 md:h-auto md:min-h-[20rem] flex items-center justify-center">
                <span className="font-serif italic text-[#9A9690] text-lg">
                  No cover yet
                </span>
              </div>
            )}

            <div className="p-7 sm:p-10 flex flex-col justify-between gap-5">
              <div>
                <div className="flex items-center gap-3 text-[11px] tracking-[0.16em] uppercase text-[#5C5852]">
                  {date && (
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar size={12} />
                      {date}
                    </span>
                  )}
                  {langCount > 1 && (
                    <span className="inline-flex items-center gap-1.5 text-[#4A6B53]">
                      <Languages size={12} />
                      {langCount} languages
                    </span>
                  )}
                </div>

                <h3 className="font-serif text-2xl sm:text-3xl text-[#2C2A28] mt-4 leading-snug">
                  {tr.title}
                </h3>
                <p className="mt-2 text-sm text-[#5C5852]">
                  Author: {blog.authorName || "Rajib Sengupta"}
                </p>
                <p className="mt-4 text-[#5C5852] leading-relaxed text-sm sm:text-base line-clamp-4">
                  {tr.synopsis}
                </p>
              </div>

              <span className="inline-flex items-center gap-2 text-sm tracking-wide text-[#4A6B53] group-hover:text-[#3A5441] transition-colors">
                Read the full post
                <ArrowUpRight
                  size={15}
                  className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-300"
                />
              </span>
            </div>
          </Link>
        </motion.article>
      </div>
    </section>
  );
}
